import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '../Button'
import { DragDropContext, Droppable } from 'react-beautiful-dnd'
import { Stack, Typography } from '@mui/material'

import {
  selectedReloadTrainingWorkoutData,
  selectedTrainingWorkoutData
} from '../../redux/reducers/training-workout/training-workout.selectors'
import { useTheme } from '@mui/material/styles'
import { useAppDispatch, useAppSelector } from '../../redux/hooks'
import {
  addTrainingWorkoutInGroup, getTrainingWorkoutList,
  reorderTrainingWorkout,
  setTrainingWorkoutType,
  showModalAddTrainingWorkout, showModalCombine
} from '../../redux/reducers/training-workout/training-workout.actions'
import { showMessage } from '../../redux/reducers/snackbar/snackbar.actions'

import { SortableItem } from './components/SortableItem'
import { SortableItemGroup } from './components/SortableItemGroup'
import { ModalCombine } from './components/ModalCombine'
import { ModalAddTrainingWorkout } from './components/ModalAddTrainingWorkout'
import { reorder } from './functions'
import { TrainingWorkoutInterface } from '../../models/interfaces/training-workout.interface'
import { ModalConfirmation } from './components/ModalConfirmation'
import { AddFieldsModal } from '../AddFieldsModal'
import { LoadingPage } from '../LoadingPage'
import { Add } from '@mui/icons-material'
import { TreatEmptySuggestion } from 'components/TreatEmptySuggestion'

export type TrainingWorkoutFormattedProps = {
  id: string
  type: 'NORMAL' | 'GROUP'
  sort: number
  data: any
}

export enum TrainingWorkoutTypeEnum {
  ATHLETE,
  TRAINING_BENCH
}

export type TrainingWorkoutProps = {
  id: string
  type: TrainingWorkoutTypeEnum
}

export const TrainingWorkout = ({ id, type }: TrainingWorkoutProps) => {
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useState(false)
  const theme = useTheme()
  const dispatch = useAppDispatch()
  const refBoxList = useRef<any>(null)
  const dataReducer = useAppSelector(selectedTrainingWorkoutData)
  const reloadTrainingWorkout = useAppSelector(selectedReloadTrainingWorkoutData)
  const [showInstruction, setShowInstruction] = useState<boolean>(false)
  const styleBox = {
    paddingRight: '7px',
    maxHeight: 'calc(100vh - 230px)',
    overflow: 'auto'
  }

  useEffect(() => {
    load().catch(console.log)
  }, [])

  useEffect(() => {
    reload().catch(console.log)
  }, [reloadTrainingWorkout])

  const reload = async () => {
    let scrollPosition = 0
    if (refBoxList.current) {
      scrollPosition = refBoxList.current.scrollTop
    }

    await dispatch(getTrainingWorkoutList({ trainingId: id, type }))
    refBoxList.current.scrollTo({ top: scrollPosition })
    setShowInstruction(dataReducer.length > 0 ? false : true)
  }

  const load = async () => {
    setIsLoading(true)
    dispatch(setTrainingWorkoutType(type))
    await dispatch(getTrainingWorkoutList({ trainingId: id, type }))
    setShowInstruction(dataReducer.length > 0 ? false : true)
    setIsLoading(false)
  }

  const handleOpenModal = () => dispatch(showModalAddTrainingWorkout({ id: String(id) }))

  async function handleDragEnd (event: any) {
    const cardMovedId = event.draggableId
    if (event.combine) {
      const cardToCombineId = event.combine.draggableId
      const itemDrag = dataReducer.find(item => item.id === cardMovedId)
      const itemDragCombined = dataReducer.find(item => item.id === cardToCombineId)

      if (!itemDrag || !itemDragCombined || (itemDrag?.type === 'GROUP' && itemDragCombined?.type === 'GROUP')) return
      if (itemDrag.type === 'GROUP') {
        if (hasSubstitutes(itemDragCombined)) {
          dispatch(showMessage({ message: t('trainingWorkout.cannotGroupWithSubstitutes'), type: 'error' }))
          return
        }
        await handleAddTrainingInGroup(itemDrag, itemDragCombined.data as TrainingWorkoutInterface)
        return
      }
      if (itemDragCombined.type === 'GROUP') {
        if (hasSubstitutes(itemDrag)) {
          dispatch(showMessage({ message: t('trainingWorkout.cannotGroupWithSubstitutes'), type: 'error' }))
          return
        }
        await handleAddTrainingInGroup(itemDragCombined, itemDrag.data as TrainingWorkoutInterface)
        return
      }
      if (hasSubstitutes(itemDrag) || hasSubstitutes(itemDragCombined)) {
        dispatch(showMessage({ message: t('trainingWorkout.cannotGroupWithSubstitutes'), type: 'error' }))
        return
      }
      dispatch(showModalCombine({ items: [cardMovedId, cardToCombineId] }))
      return
    }
    if (!event.destination || event.source.index === event.destination.index) {
      return
    }
    const payload = reorder(dataReducer, event.source.index, event.destination.index)
    await dispatch(reorderTrainingWorkout({
      trainingId: id,
      type,
      payload
    }))
    await reload()
  }

  const hasSubstitutes = (item: TrainingWorkoutFormattedProps): boolean => {
    return (item.data?.substitutes?.length ?? 0) > 0
  }

  const handleAddTrainingInGroup = async (
    itemGroup: TrainingWorkoutFormattedProps,
    trainingWorkout: TrainingWorkoutInterface
  ): Promise<void> => {
    await dispatch(addTrainingWorkoutInGroup({
      payload: { trainingWorkoutId: trainingWorkout.id },
      type,
      groupTrainingWorkoutId: itemGroup.id
    }))
    await reload()
  }

  const getListStyle = (isDraggingOver: boolean) => ({
    background: isDraggingOver ? theme.palette.grey[600] : 'none',
    padding: isDraggingOver ? 10 : 0
  })

  return (
    <Stack mt={2} mb={0}>
      { dataReducer?.length === 0 && <Stack direction={{ xs: 'column', sm: 'row', md: 'row' }} flex={1} justifyContent="right">
        <Button
          variant="contained" color="primary"
          title={`${t('trainingWorkout.addWorkout')}`}
          startIcon={<Add />}
          onClick={handleOpenModal}
        />
      </Stack>
      }
      <Stack justifyContent="center" direction="column">
        {isLoading && <LoadingPage />}
        {dataReducer?.length === 0 &&
          <TreatEmptySuggestion
            textSuggestion={[
              t('trainingWorkout.title.suggestion_1', { addWorkout: t('trainingWorkout.addWorkout') })
            ]}
            showInstruction={showInstruction}
          />
        }
        {dataReducer?.length > 0 &&
          <>
            <Stack
              justifyContent="space-between"
              direction={{ xs: 'column', sm: 'row' }}
              spacing={{ xs: 2, sm: 4, md: 6 }}
              margin={0.5}
            >
              <Stack
                direction="row"
                flex={1}>
                <Typography color={theme.palette.primary.main}>
                  <b>{t('workout.group')}</b>
                </Typography>
              </Stack>
              <Stack>
                <Button
                  variant="contained" color="primary"
                  title={`${t('trainingWorkout.addWorkout')}`}
                  startIcon={<Add />}
                  onClick={handleOpenModal}
                />
              </Stack>
            </Stack>
            <Stack ref={refBoxList} style={styleBox}>
              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="droppable" isCombineEnabled={true}>
                  {(provided, snapshot) => (
                    <Stack
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      style={getListStyle(snapshot.isDraggingOver)}>
                      {dataReducer?.map((item, index) => {
                        if (item.type === 'NORMAL') {
                          return (
                            <Stack key={index}>
                              <SortableItem key={index} data={item.data} index={index} trainingId={String(id)} />
                            </Stack>
                          )
                        }
                        return (
                          <Stack key={index}>
                            <SortableItemGroup id={item.id} key={index} data={item.data} index={index} trainingId={String(id)} />
                          </Stack>
                        )
                      })}
                      {provided.placeholder}
                    </Stack>
                  )}
                </Droppable>
              </DragDropContext>
            </Stack>
          </>
        }
      </Stack>

      <ModalCombine />
      <ModalAddTrainingWorkout />
      <ModalConfirmation />
      <AddFieldsModal />

    </Stack>
  )
}
